/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { capitalize, inflect, isNotEmpty, isNotNil } from "~/tools";
import { Item } from "~/types";
import { Mode, Option, OptionKey } from "./types";

export const getOptionValue = (
  option: Option | undefined,
  key: OptionKey = "value"
) => {
  if (option === undefined) {
    return undefined;
  }

  return typeof option === "string" ? option : option[key];
};

export const getFirstOptionLabel = (
  selectedOptions: string[],
  options: readonly Item[]
) => {
  return options.find((option) => option.id === selectedOptions[0])?.name;
};

export const getMultiSelectLabel = (
  selectedCount: number,
  name: string,
  pluralName?: string
) => {
  const formatter = inflect(name, pluralName);
  const words = formatter(selectedCount).split(" ");
  const capitalizedName = words.map((word) => capitalize(word)).join(" ");
  return `${selectedCount} ${capitalizedName} Selected`;
};

export function getSelectionsText(selectedOptions?: string[]) {
  if (isNotNil(selectedOptions) && isNotEmpty(selectedOptions)) {
    return `Selected: ${selectedOptions.length}`;
  }

  return "";
}

export function getDateRangeSelectionsText(
  startDate?: string,
  endDate?: string,
  modeLabel = "Include"
) {
  if (!startDate || !endDate) {
    return undefined;
  }

  return `${modeLabel}: From ${dayjs(startDate).format(
    "DD.MM.YYYY"
  )} to ${dayjs(endDate).format("DD.MM.YYYY")}`;
}

export function getNestedSelectionsText({
  primaryCount,
  primaryLabel,
  secondaryCount,
  secondaryLabel,
  modeLabel = "Include",
}: {
  primaryCount: number;
  primaryLabel: string;
  secondaryCount: number;
  secondaryLabel: string;
  modeLabel?: string;
}) {
  if (primaryCount <= 0 && secondaryCount <= 0) {
    return undefined;
  }

  const primaryPart =
    primaryCount > 0
      ? `${primaryCount} ${inflect(primaryLabel)(primaryCount)}`
      : undefined;
  const secondaryPart =
    secondaryCount > 0
      ? `${secondaryCount} ${inflect(secondaryLabel)(secondaryCount)}`
      : undefined;
  const parts = [primaryPart, secondaryPart].filter(isNotNil);

  return `${modeLabel}: ${parts.join(" and ")}`;
}

export function getSelectedMode(excluded: boolean | undefined) {
  return excluded ? Mode.EXCLUDES : Mode.INCLUDES;
}
